// React
import { useCallback, useContext, useEffect, useRef, useState } from "react";

// @swiggy-private/js-utils
import { isEmpty } from "@swiggy-private/js-utils";

// @swiggy-private/portal-dineout-context
import {
    IApiConfigs,
    PinGenerationStatus,
    QrCodeActions,
    ResponseContext,
    SwiggyContextHeader,
    TableContext,
    usePlApiContextFetch,
} from "@swiggy-private/portal-dineout-context";

// App
import { WebStorageLocation } from "types";
import { IPLWidgetControllerCards } from "utils/types";

// Relative
import { fetchMenuData } from "../api";
import { NR_TABLES, STORAGE_KEYS } from "../constants";
import { getFromStorage } from "../utils";
import { menuTransformer, updateMenuItemInDish, updateMenuUI, ingestPinInMenuResponse } from "../utils/menu";
import { sendCustomNREvViaBlaze } from "../utils/newrelic";

export const useMenuData = (hideBottomBar: () => void) => {
    const { swiggyHeaders } = useContext(SwiggyContextHeader);
    const { tableId = "", restaurantId = "" } = useContext(TableContext);
    const { state, dispatch } = useContext(ResponseContext);
    const makePlApiCall = usePlApiContextFetch();

    const [loading, setLoading] = useState(true);
    const [showApiError, setShowApiError] = useState(false);
    const hasMenuFetchedRef = useRef(false);

    const hasInitialData = !isEmpty(state.menu.initialData);
    const isMenuLoading = state.menu.loading;
    const hasMenuError = state.menu.error;

    // Fallback: Only fetch if we're not on a menu page route or prefetch didn't handle it
    // This is an edge case - normally prefetch handles all menu page API calls
    const fetchMenuDataFallback = useCallback(async () => {
        if (hasMenuFetchedRef.current || !restaurantId || !tableId || !makePlApiCall) return;

        hasMenuFetchedRef.current = true;
        try {
            dispatch({ type: QrCodeActions.MENU_SCREEN_LOADING, payload: {} });

            let finalLocalCart = {};
            try {
                const storageResponse = await getFromStorage(STORAGE_KEYS.LOCAL_CART, WebStorageLocation.SESSION, true);
                const localCartString = storageResponse?.data?.[STORAGE_KEYS.LOCAL_CART] || "{}";
                const localCart = JSON.parse(localCartString);
                finalLocalCart = localCart || {};
                dispatch({ type: QrCodeActions.UPDATE_LOCAL_CART, payload: finalLocalCart });
            } catch (_) {
                sendCustomNREvViaBlaze(NR_TABLES.SESSION_STORAGE_TABLE_CONFIG_ERROR, {}, swiggyHeaders);
            }

            const menuResponse = await fetchMenuData({ tableId, restaurantId }, makePlApiCall);

            if (menuResponse?.statusCode === 1) {
                setShowApiError(true);
                if (menuResponse.failure) {
                    hideBottomBar();
                    setLoading(false);
                    return;
                }
            }

            if (!isEmpty(menuResponse) && !isEmpty(menuResponse?.success)) {
                const { menuDataAfterDietOptions, selectedTab: currentSelectedTab } = menuTransformer(
                    menuResponse.success || [],
                ) as { menuDataAfterDietOptions: IPLWidgetControllerCards[]; selectedTab: string };

                const updatedTransformedMenuResponse = updateMenuItemInDish(
                    menuDataAfterDietOptions || [],
                    finalLocalCart,
                );

                dispatch({ type: QrCodeActions.MENU_SCREEN_SUCCESS, payload: {} });
                dispatch({
                    type: QrCodeActions.MENU_SCREEN_UPDATE_REQUEST_ID,
                    payload: { requestId: menuResponse?.requestId || "" },
                });
                updateMenuUI(updatedTransformedMenuResponse, currentSelectedTab, dispatch);

                // Update configs in state
                const configs = menuResponse.success?.configs as IApiConfigs;
                if (configs) {
                    dispatch({
                        type: QrCodeActions.MENU_SCREEN_UPDATE_CONFIGS,
                        payload: { configs },
                    });
                }

                // Always inject pin if pin data exists (regardless of timing)
                const hasPinData = Boolean(state.pin?.data?.status);
                if (hasPinData && state.pin?.data) {
                    ingestPinInMenuResponse(
                        updatedTransformedMenuResponse,
                        {
                            selectedTab: currentSelectedTab,
                            pin: state.pin.data.pin,
                            tableNo: state.pin.data.tableNo,
                            status: state.pin.data.status as PinGenerationStatus,
                        },
                        dispatch,
                    );
                }
            }
            setLoading(false);
        } catch {
            setLoading(false);
            hasMenuFetchedRef.current = false;
        }
    }, [restaurantId, tableId, dispatch, makePlApiCall, swiggyHeaders, hideBottomBar, state.pin?.data]);

    useEffect(() => {
        // If menu API is being called by prefetch hook, wait for it
        // This is the primary check - prefetch handles all menu page API calls
        if (isMenuLoading) {
            return;
        }

        // If prefetch completed with error, show error state
        if (hasMenuError && !hasInitialData) {
            setLoading(false);
            setShowApiError(true);
            hideBottomBar();
            return;
        }
        // If data exists in state (from prefetch), use it
        // Always update menuData when initialData changes to get fresh data after refetch
        if (hasInitialData) {
            hasMenuFetchedRef.current = true;
            setLoading(false);
            return;
        }
    }, [
        restaurantId,
        tableId,
        dispatch,
        makePlApiCall,
        swiggyHeaders,
        hideBottomBar,
        hasInitialData,
        isMenuLoading,
        hasMenuError,
        state.menu.initialData,
        fetchMenuDataFallback,
    ]);

    return { loading, showApiError };
};
